/**
 * What a resolved basket costs, or pays — ROADMAP 5.6.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SIGN CONVENTION, STATED ONCE
 *
 *   POSITIVE  =  net CREDIT   — money received for opening the position
 *   NEGATIVE  =  net DEBIT    — money paid to open it
 *
 * Picked once and enforced by test, because the alternative is the failure
 * this project has already seen elsewhere: one writer with the opposite sign
 * and a number that is wrong by twice itself. Everything downstream reads the
 * sign rather than re-deriving it from the legs.
 *
 * PRICED OFF THE BOOK, NOT THE LAST TRADE
 *
 * A short strangle does not pay you its last traded price. You are filled on
 * the BID when selling and the ASK when buying, and on an illiquid far strike
 * the gap between that and the LTP is most of the edge. Quoting a credit off
 * LTP flatters every short-premium strategy in the catalogue, which are
 * exactly the ones a beginner reaches for.
 *
 * `[B18]` already established this for order pricing. The number a trader
 * DECIDES on must come from the same place the fill will.
 *
 * NO FALLBACK TO LTP
 *
 * A missing side of the book means the basket cannot be priced, and it says
 * so. Substituting the last trade would answer a different question with the
 * confident shape of an answer to this one.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * Price a resolved basket.
 *
 * `lotSize` is the contract multiplier for this underlying — one per basket,
 * because every leg of a strategy shares an underlying and an expiry.
 */
export function netPremiumOf(legs, lotSize) {
    if (legs.length === 0)
        return { ok: false, why: 'Nothing to price.' };
    if (!Number.isInteger(lotSize) || lotSize <= 0) {
        return { ok: false, why: `Lot size must be a positive whole number, got ${lotSize}.` };
    }
    const perLeg = [];
    let netPremium = 0;
    for (const leg of legs) {
        /*
         * The side you CROSS. Selling lifts the bid, buying takes the ask — the
         * opposite choice would quote the price of the trade nobody is making.
         */
        const price = leg.side === 'SELL' ? leg.bid : leg.ask;
        if (price === undefined || !(price > 0)) {
            const side = leg.side === 'SELL' ? 'bid' : 'ask';
            return {
                ok: false,
                why: `No ${side} on ${leg.strike} ${leg.optionType}, so this basket cannot be priced.`,
            };
        }
        const units = leg.lots * lotSize;
        // Sold legs bring money in, bought legs take it out.
        const value = (leg.side === 'SELL' ? 1 : -1) * price * units;
        perLeg.push({ token: leg.token, price, value });
        netPremium += value;
    }
    return { ok: true, netPremium: round2(netPremium), perLeg };
}
/**
 * Rupees, to the paisa.
 *
 * Summing floats across four legs leaves trailing noise — 14099.999999999998
 * renders as a number nobody would type. Rounded once at the end rather than
 * per leg, so the per-leg values still add to the total exactly.
 */
const round2 = (v) => Math.round(v * 100) / 100;
/**
 * Margin for a multi-leg position — DELIBERATELY NOT IMPLEMENTED.
 *
 * SPAN + exposure for a spread is not computable from anything we hold, and
 * `[B9]` records that this gateway does not serve
 * `/interactive/orders/margindetails` — it answers `e-calculatemargin-0006
 * "Data not found"` for every combination tried.
 *
 * So there is no margin number, and this function exists to make that
 * explicit rather than leaving a caller to wonder where it went. Every
 * competitor shows one; showing a WRONG one is worse than showing none,
 * because it is the figure a person sizes a short-premium position on.
 *
 * To close it: either a local SPAN implementation against the exchange's
 * published files, or a broker that serves a basket-margin endpoint. Open
 * question #1 in `docs/XTS-STOXXO-FAILURE-RESEARCH.md`.
 */
export function marginFor() {
    return {
        known: false,
        why: 'Margin is not available: this broker gateway does not serve a margin endpoint, and SPAN for a spread cannot be derived from prices alone.',
    };
}
//# sourceMappingURL=strategyPremium.js.map